/**
 * LayoutManager - Orchestrates multiple zones on a screen
 * Handles layout changes and zone lifecycle
 */

import { ZoneRenderer, loadCustomFonts } from './zone-renderer';
import type {
  ManifestLayout,
  ManifestZone,
  DeviceManifest,
  PlayEvent,
} from '../types';

type PlayEventCallback = (event: PlayEvent) => void;

/**
 * Default full-screen layout for backward compatibility
 * Used when device has no layout assigned
 */
const DEFAULT_LAYOUT: ManifestLayout = {
  id: 'default-fullscreen',
  name: 'Full Screen',
  width: 1920,
  height: 1080,
  orientation: 'LANDSCAPE',
  zones: [
    {
      id: 'main',
      name: 'Main',
      x: 0,
      y: 0,
      width: 100,
      height: 100,
      zIndex: 0,
      contentType: 'PLAYLIST',
      fitMode: 'FIT',
    },
  ],
};

export class LayoutManager {
  private container: HTMLElement | null = null;
  private zones: Map<string, ZoneRenderer> = new Map();
  private currentLayout: ManifestLayout | null = null;
  private manifest: DeviceManifest | null = null;
  private onPlayEvent: PlayEventCallback | null = null;

  /**
   * Initialize the layout manager with a container element
   */
  initialize(containerId: string): void {
    this.container = document.getElementById(containerId);

    if (!this.container) {
      throw new Error(`Container element not found: ${containerId}`);
    }

    // Clear any existing content from previous renderer mode
    this.stop();
    this.container.innerHTML = '';

    // Set up container styles for layout management
    this.container.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #000;
    `;

    console.log('[LayoutManager] Initialized multi-zone mode');
  }

  /**
   * Set callback for play events from all zones
   */
  setPlayEventCallback(callback: PlayEventCallback): void {
    this.onPlayEvent = callback;
  }

  /**
   * Set the manifest and update the layout
   */
  async setManifest(manifest: DeviceManifest): Promise<void> {
    this.manifest = manifest;
    // Load custom brand fonts before rendering
    await loadCustomFonts(manifest.fonts);
  }

  /**
   * Apply a layout to the screen
   * Creates zone renderers for each zone in the layout
   */
  async setLayout(layout: ManifestLayout | null): Promise<void> {
    if (!this.container) {
      throw new Error('LayoutManager not initialized');
    }

    // Use default layout if none provided
    const effectiveLayout = layout || DEFAULT_LAYOUT;

    // Check if layout actually changed
    if (this.currentLayout?.id === effectiveLayout.id) {
      console.log('Layout unchanged, skipping update');
      return;
    }

    console.log(`Setting layout: ${effectiveLayout.name} (${effectiveLayout.zones.length} zones)`);

    // Clear existing zones
    this.clearZones();

    // Create new zones
    for (const zoneConfig of effectiveLayout.zones) {
      const zoneRenderer = new ZoneRenderer(zoneConfig, this.container);

      // Set up play event callback
      if (this.onPlayEvent) {
        zoneRenderer.setPlayEventCallback(this.onPlayEvent);
      }

      this.zones.set(zoneConfig.id, zoneRenderer);
    }

    this.currentLayout = effectiveLayout;
  }

  /**
   * Start content playback in all zones
   * Requires manifest to be set first
   */
  async startPlayback(): Promise<void> {
    if (!this.manifest) {
      console.warn('No manifest set, cannot start playback');
      return;
    }

    if (!this.currentLayout) {
      console.warn('No layout set, using default');
      await this.setLayout(null);
    }

    console.log(`[LayoutManager] Starting playback for ${this.zones.size} zones`);
    console.log('[LayoutManager] Current layout:', JSON.stringify(this.currentLayout, null, 2));
    console.log('[LayoutManager] Manifest playlists:', this.manifest.playlists.map(p => ({ id: p.id, name: p.name })));
    console.log('[LayoutManager] Active playlist ID:', this.manifest.schedule.activePlaylistId);

    // Start content in each zone
    for (const [, zoneRenderer] of this.zones) {
      const zoneConfig = zoneRenderer.getZone();

      console.log(`[LayoutManager] Processing zone "${zoneConfig.name}":`, {
        id: zoneConfig.id,
        contentType: zoneConfig.contentType,
        hasContent: !!zoneConfig.content,
        contentObj: zoneConfig.content,
        playlistId: zoneConfig.playlistId,
        visible: zoneConfig.visible,
        position: { x: zoneConfig.x, y: zoneConfig.y, w: zoneConfig.width, h: zoneConfig.height },
      });

      // Skip hidden zones
      if (zoneConfig.visible === false) {
        console.log(`[LayoutManager] Zone ${zoneConfig.name}: Hidden, skipping`);
        continue;
      }

      // Check if we have flexible content configuration
      if (zoneConfig.content?.type) {
        console.log(`[LayoutManager] Zone ${zoneConfig.name}: Using flexible content type "${zoneConfig.content.type}"`);
        await this.startFlexibleContentInZone(zoneRenderer, zoneConfig);
        continue;
      }

      // Fallback to legacy contentType-based routing
      console.log(`[LayoutManager] Zone ${zoneConfig.name}: Using legacy contentType "${zoneConfig.contentType}"`);
      switch (zoneConfig.contentType) {
        case 'PLAYLIST':
          await this.startPlaylistInZone(zoneRenderer, zoneConfig);
          break;

        case 'DESIGN':
          await this.startDesignInZone(zoneRenderer, zoneConfig);
          break;

        case 'ASSET':
          await this.startAssetInZone(zoneRenderer, zoneConfig);
          break;

        case 'TEXT':
          await this.startTextInZone(zoneRenderer, zoneConfig);
          break;

        case 'WIDGET':
          await this.startWidgetInZone(zoneRenderer, zoneConfig);
          break;

        case 'EMPTY':
          console.log(`Zone ${zoneConfig.name}: Empty zone`);
          break;

        default:
          console.warn(`Zone ${zoneConfig.name}: Unknown content type: ${zoneConfig.contentType}`);
      }
    }
  }

  /**
   * Handle flexible content configuration
   */
  private async startFlexibleContentInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    const content = zoneConfig.content!;

    switch (content.type) {
      case 'playlist':
        if (content.playlistId) {
          zoneConfig.playlistId = content.playlistId;
          await this.startPlaylistInZone(zoneRenderer, zoneConfig);
        }
        break;

      case 'asset':
        if (content.assetId) {
          zoneConfig.assetId = content.assetId;
          await this.startAssetInZone(zoneRenderer, zoneConfig);
        }
        break;

      case 'text':
        if (content.text) {
          zoneRenderer.renderText(content.text);
        }
        break;

      case 'widget':
        if (content.widgetType) {
          zoneRenderer.renderWidget(content.widgetType, content.widgetConfig || {});
        }
        break;

      case 'empty':
      default:
        // For empty zones, check if it's the main zone and should get fallback playlist
        const isMainZone = zoneConfig.id === 'main' ||
                          zoneConfig.name?.toLowerCase() === 'main' ||
                          zoneConfig.name?.toLowerCase() === 'ana';
        if (isMainZone && this.manifest?.schedule?.activePlaylistId) {
          console.log(`Zone ${zoneConfig.name}: Empty main zone, using fallback playlist`);
          zoneConfig.playlistId = this.manifest.schedule.activePlaylistId;
          await this.startPlaylistInZone(zoneRenderer, zoneConfig);
        } else {
          console.log(`Zone ${zoneConfig.name}: Empty, no content to render`);
        }
    }
  }

  /**
   * Start a playlist in a specific zone
   */
  private async startPlaylistInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    if (!this.manifest) return;

    // Determine which playlist to play
    let playlistId = zoneConfig.playlistId;

    // Only use fallback playlist for the main zone (first zone or zone named 'main')
    // Other zones should remain empty if no specific content is assigned
    if (!playlistId) {
      const isMainZone = zoneConfig.id === 'main' ||
                         zoneConfig.name?.toLowerCase() === 'main' ||
                         zoneConfig.name?.toLowerCase() === 'ana';

      if (isMainZone) {
        playlistId = this.manifest.schedule.activePlaylistId;
      }
    }

    if (!playlistId) {
      console.log(`Zone ${zoneConfig.name}: No playlist assigned, staying empty`);
      return;
    }

    const playlist = this.manifest.playlists.find((p) => p.id === playlistId);

    if (!playlist) {
      console.warn(`Zone ${zoneConfig.name}: Playlist not found: ${playlistId}`);
      return;
    }

    // Get assets for this playlist (filter out layout items which have no assetId)
    const playlistAssetIds = new Set(playlist.items.filter((i) => i.assetId).map((i) => i.assetId!));
    const playlistAssets = this.manifest.assets.filter((a) => playlistAssetIds.has(a.id));

    console.log(
      `Zone ${zoneConfig.name}: Starting playlist ${playlist.name} (${playlist.items.length} items)`
    );

    zoneRenderer.startPlaylist(playlist, playlistAssets);
  }

  /**
   * Start a design in a specific zone
   */
  private async startDesignInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    if (!this.manifest || !zoneConfig.designId) {
      console.warn(`Zone ${zoneConfig.name}: No design assigned`);
      return;
    }

    const design = this.manifest.designs?.find((d) => d.id === zoneConfig.designId);

    if (!design) {
      console.warn(`Zone ${zoneConfig.name}: Design not found: ${zoneConfig.designId}`);
      return;
    }

    console.log(`Zone ${zoneConfig.name}: Rendering design ${design.name}`);

    zoneRenderer.renderDesign(design);
  }

  /**
   * Start a single asset in a specific zone
   */
  private async startAssetInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    if (!this.manifest || !zoneConfig.assetId) {
      console.warn(`Zone ${zoneConfig.name}: No asset assigned`);
      return;
    }

    const asset = this.manifest.assets.find((a) => a.id === zoneConfig.assetId);

    if (!asset) {
      console.warn(`Zone ${zoneConfig.name}: Asset not found: ${zoneConfig.assetId}`);
      return;
    }

    console.log(`Zone ${zoneConfig.name}: Rendering asset ${asset.id}`);

    zoneRenderer.renderSingleAsset(asset, zoneConfig.fitMode);
  }

  /**
   * Start text content in a specific zone
   */
  private async startTextInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    const textContent = zoneConfig.content?.text;
    if (!textContent) {
      console.warn(`Zone ${zoneConfig.name}: No text content configured`);
      return;
    }

    console.log(`Zone ${zoneConfig.name}: Rendering text`);

    zoneRenderer.renderText(textContent);
  }

  /**
   * Start widget in a specific zone
   */
  private async startWidgetInZone(
    zoneRenderer: ZoneRenderer,
    zoneConfig: ManifestZone
  ): Promise<void> {
    const widgetType = zoneConfig.content?.widgetType;
    if (!widgetType) {
      console.warn(`Zone ${zoneConfig.name}: No widget type configured`);
      return;
    }

    console.log(`Zone ${zoneConfig.name}: Rendering ${widgetType} widget`);

    zoneRenderer.renderWidget(widgetType, zoneConfig.content?.widgetConfig || {});
  }

  /**
   * Render a layout temporarily as a playlist item.
   * Creates a temporary multi-zone container over the player,
   * starts zone playback, and returns a cleanup function.
   */
  async renderTemporaryLayout(
    layout: ManifestLayout,
    manifest: DeviceManifest,
    containerId: string
  ): Promise<() => void> {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container not found: ${containerId}`);
    }

    // Create a temporary overlay container for the layout
    const tempContainer = document.createElement('div');
    tempContainer.id = `temp-layout-${layout.id}`;
    tempContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 10;
      overflow: hidden;
      background: #000;
    `;
    container.appendChild(tempContainer);

    // Create zone renderers for this temporary layout
    const tempZones: ZoneRenderer[] = [];

    for (const zoneConfig of layout.zones) {
      const zoneRenderer = new ZoneRenderer(zoneConfig, tempContainer);

      if (this.onPlayEvent) {
        zoneRenderer.setPlayEventCallback(this.onPlayEvent);
      }

      tempZones.push(zoneRenderer);

      // Skip hidden zones
      if (zoneConfig.visible === false) continue;

      // Start content in zone using manifest data
      if (zoneConfig.content?.type) {
        // Flexible content type
        switch (zoneConfig.content.type) {
          case 'playlist':
            if (zoneConfig.content.playlistId) {
              const playlist = manifest.playlists.find((p) => p.id === zoneConfig.content!.playlistId);
              if (playlist) {
                const playlistAssetIds = new Set(playlist.items.filter(i => i.assetId).map((i) => i.assetId!));
                const playlistAssets = manifest.assets.filter((a) => playlistAssetIds.has(a.id));
                zoneRenderer.startPlaylist(playlist, playlistAssets);
              }
            }
            break;
          case 'asset':
            if (zoneConfig.content.assetId) {
              const asset = manifest.assets.find((a) => a.id === zoneConfig.content!.assetId);
              if (asset) zoneRenderer.renderSingleAsset(asset, zoneConfig.fitMode);
            }
            break;
          case 'text':
            if (zoneConfig.content.text) zoneRenderer.renderText(zoneConfig.content.text);
            break;
          case 'widget':
            if (zoneConfig.content.widgetType) {
              zoneRenderer.renderWidget(zoneConfig.content.widgetType, zoneConfig.content.widgetConfig || {});
            }
            break;
        }
      } else if (zoneConfig.contentType === 'PLAYLIST') {
        // Legacy content type
        const playlistId = zoneConfig.playlistId || manifest.schedule.activePlaylistId;
        const playlist = manifest.playlists.find((p) => p.id === playlistId);
        if (playlist) {
          const playlistAssetIds = new Set(playlist.items.filter(i => i.assetId).map((i) => i.assetId!));
          const playlistAssets = manifest.assets.filter((a) => playlistAssetIds.has(a.id));
          zoneRenderer.startPlaylist(playlist, playlistAssets);
        }
      } else if (zoneConfig.contentType === 'ASSET' && zoneConfig.assetId) {
        const asset = manifest.assets.find((a) => a.id === zoneConfig.assetId);
        if (asset) zoneRenderer.renderSingleAsset(asset, zoneConfig.fitMode);
      }
    }

    console.log(`[LayoutManager] Rendered temporary layout "${layout.name}" with ${tempZones.length} zones`);

    // Return cleanup function
    return () => {
      for (const zoneRenderer of tempZones) {
        zoneRenderer.stop();
        zoneRenderer.destroy();
      }
      if (tempContainer.parentElement) {
        tempContainer.parentElement.removeChild(tempContainer);
      }
      console.log(`[LayoutManager] Cleaned up temporary layout "${layout.name}"`);
    };
  }

  /**
   * Stop all zone playback
   */
  stop(): void {
    for (const zoneRenderer of this.zones.values()) {
      zoneRenderer.stop();
    }
  }

  /**
   * Clear all zones
   */
  clearZones(): void {
    for (const zoneRenderer of this.zones.values()) {
      zoneRenderer.destroy();
    }
    this.zones.clear();
    this.currentLayout = null;
  }

  /**
   * Destroy the layout manager
   */
  destroy(): void {
    this.clearZones();
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.manifest = null;
  }

  /**
   * Get current layout
   */
  getCurrentLayout(): ManifestLayout | null {
    return this.currentLayout;
  }

  /**
   * Get all zone renderers
   */
  getZones(): Map<string, ZoneRenderer> {
    return this.zones;
  }

  /**
   * Check if using default layout (no layout assigned)
   */
  isUsingDefaultLayout(): boolean {
    return this.currentLayout?.id === DEFAULT_LAYOUT.id;
  }
}

// Export singleton instance
export const layoutManager = new LayoutManager();
